import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell } from "lucide-react";
import * as Icons from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";
import { markActivitiesRead } from "@/services/activities";
import { useNavigate } from "react-router-dom";

type Activity = {
  id: string;
  title: string;
  description: string | null;
  created_at: string;
  read_at: Record<string, string> | null;
  category_id: string | null;
  category_screen_id: string | null;
  category_entity_id: string | null;
  category_operation_id: string | null;
};

export function NotificationsBell() {
  const { tenantId } = useTenant();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"read" | "unread" | "all">("unread");
  const [userId, setUserId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => setUserId(user?.id ?? null));
  }, []);

  const { data: activities = [] } = useQuery<any[]>({
    queryKey: ["activities", tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error } = await (supabase as any)
        .from("activities")
        .select(`
          id, title, description, created_at, read_at, metadata, created_by,
          creator:created_by(id,nome_completo),
          category_entity:category_activities!activities_category_entity_id_fkey(icon,name,color,slug),
          category_screen:category_activities!activities_category_screen_id_fkey(icon,name,color,slug),
          category_operation:category_activities!activities_category_operation_id_fkey(icon,name,color,slug)
        `)
        .eq("tenant_id", tenantId)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data || [];
    },
  });

  const unreadCount = useMemo(() => {
    if (!activities || !userId) return 0;
    return activities.filter(a => !(a.read_at && (a.read_at as any)[userId])).length;
  }, [activities, userId]);

  const isReadForUser = (a: any) => {
    if (!userId) return false;
    return !!(a.read_at && (a.read_at as any)[userId]);
  };

  const filteredActivities = useMemo(() => {
    if (activeTab === "all") return activities;
    if (activeTab === "read") return activities.filter(a => isReadForUser(a));
    return activities.filter(a => !isReadForUser(a));
  }, [activities, activeTab, userId]);

  useEffect(() => {
    if (open) setActiveTab("unread");
  }, [open]);

  useEffect(() => {
    if (!tenantId) return;
    const channel = supabase.channel("activities-listener").on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "activities", filter: `tenant_id=eq.${tenantId}` },
      () => { queryClient.invalidateQueries({ queryKey: ["activities", tenantId] }); }
    ).subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [tenantId, queryClient]);

  const handleMarkAllRead = async () => {
    const ids = activities.map(a => a.id);
    if (ids.length === 0) return;
    if (userId) {
      queryClient.setQueryData(["activities", tenantId], (prev: any) => {
        if (!Array.isArray(prev)) return prev;
        const now = new Date().toISOString();
        return prev.map((a: any) => ({
          ...a,
          read_at: { ...(a.read_at || {}), [userId]: now },
        }));
      });
    }
    await markActivitiesRead(ids);
    queryClient.invalidateQueries({ queryKey: ["activities", tenantId] });
  };

  const navigate = useNavigate();
  const getIconComponent = (name?: string | null) => {
    if (!name) return null;
    const pascal = String(name).replace(/[-_\s]+/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('');
    return (Icons as any)[pascal] || null;
  };
  const getIconFallbackByEntity = (entityTable?: string | null) => {
    switch (String(entityTable || "")) {
      case "products": return (Icons as any)["Package"];
      case "clients": return (Icons as any)["Users"];
      case "suppliers": return (Icons as any)["UserSquare"];
      case "product_categories": return (Icons as any)["Tag"];
      case "expense_categories": return (Icons as any)["Tag"];
      case "forms": return (Icons as any)["FileText"];
      case "kb_documents": return (Icons as any)["FileText"];
      case "knowledge_bases": return (Icons as any)["Database"];
      case "bank_accounts": return (Icons as any)["Wallet"];
      case "cash_register_sessions": return (Icons as any)["Wallet"];
      case "transactions": return (Icons as any)["Receipt"];
      case "subscription_coupons": return (Icons as any)["Ticket"];
      case "subscription_transactions": return (Icons as any)["Receipt"];
      case "profiles": return (Icons as any)["User"];
      case "roles": return (Icons as any)["UserCog"];
      case "permissions": return (Icons as any)["Lock"];
      case "tenants": return (Icons as any)["Building2"];
      default: return null;
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Notificações" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 px-1 rounded-full text-[10px] flex items-center justify-center border"
              style={{ backgroundColor: '#0b3c8a', color: '#fff', borderColor: '#fff' }}
            >
              {unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96">
        <div className="p-2 border-b flex items-center gap-2">
          <Button variant={activeTab === "unread" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("unread")}>Não lidas</Button>
          <Button variant={activeTab === "read" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("read")}>Lidas</Button>
          <Button variant={activeTab === "all" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("all")}>Todas</Button>
        </div>
        {filteredActivities.length === 0 ? (
          <div className="p-4 text-sm">Sem atividades</div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {filteredActivities.map(a => {
              let EntIcon = getIconComponent(a?.category_entity?.icon);
              if (!EntIcon) EntIcon = getIconFallbackByEntity(a?.metadata?.entity_table as string | undefined);
              if (!EntIcon) EntIcon = getIconComponent(a?.category_operation?.icon) || (Icons as any)["MousePointer"];
              const screen = a?.category_screen;
              const linkPath = a?.metadata?.link_path as string | undefined;
              const changes = a?.metadata?.changes as Record<string, { before: any; after: any }> | undefined;
              const fallbackScreenName = (() => {
                if (screen?.name) return null;
                if (!linkPath) return null;
                const seg = String(linkPath).replace(/^\//,'');
                if (!seg) return null;
                const name = seg.split('/')[0] || seg;
                return name;
              })();
              const handleClick = async (e?: any) => {
                if (e && typeof e.preventDefault === 'function') e.preventDefault();
                if (!isReadForUser(a) && userId) {
                  const now = new Date().toISOString();
                  queryClient.setQueryData(["activities", tenantId], (prev: any) => {
                    if (!Array.isArray(prev)) return prev;
                    return prev.map((item: any) => item.id === a.id ? { ...item, read_at: { ...(item.read_at || {}), [userId]: now } } : item);
                  });
                  await markActivitiesRead([a.id]);
                }
              };
              return (
                <DropdownMenuItem
                  key={a.id}
                  className="flex flex-col items-start gap-1 cursor-pointer border-b last:border-b-0"
                  onClick={(e) => handleClick(e)}
                  onSelect={(e) => e.preventDefault()}
                >
                  <div className="flex items-center gap-2 w-full">
                    {EntIcon ? <EntIcon className="h-4 w-4" /> : null}
                    <div className="text-sm font-medium flex-1">{a.title}</div>
                    {screen?.name ? (
                      <Badge
                        style={{ backgroundColor: screen?.color || undefined, color: '#fff', fontSize: '11px', padding: '1px 6px', lineHeight: '14px' }}
                        className="text-xs"
                      >
                        {screen.name}
                      </Badge>
                    ) : fallbackScreenName ? (
                      <Badge variant="secondary" className="text-xs text-[11px]" style={{ color: '#fff', padding: '1px 6px', lineHeight: '14px' }}>
                        {fallbackScreenName}
                      </Badge>
                    ) : null}
                  </div>
                  {changes && Object.keys(changes).length > 0 ? (
                    <div className="text-xs w-full" style={{ display: 'none' }}>
                      {Object.entries(changes)
                        .filter(([key]) => !["updated_at","updatedAt"].includes(key))
                        .slice(0, 2)
                        .map(([k, v]) => {
                        const before = v?.before;
                        const after = v?.after;
                        const fmt = (val: any) => (val === undefined || val === null || val === "undefined") ? "—" : String(val);
                        return (
                          <div key={k} className="flex gap-1">
                            <span className="text-muted-foreground">{k}:</span>
                            <span className="text-red-600">{fmt(before)}</span>
                            <span className="text-muted-foreground">→</span>
                            <span className="text-green-600">{fmt(after)}</span>
                          </div>
                        );
                      })}
                      {Object.keys(changes).filter(k => !["updated_at","updatedAt"].includes(k)).length > 2 ? <div className="text-muted-foreground">…</div> : null}
                    </div>
                  ) : (() => {
                    const desc = a?.description as string | undefined;
                    const hasEntitySlug = desc ? /entity:/i.test(desc) : false;
                    const hasUuid = desc ? /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(desc) : false;
                    if (!a?.metadata?.path && desc && !hasEntitySlug && !hasUuid) {
                      return <div className="text-xs text-muted-foreground">{desc}</div>;
                    }
                    return null;
                  })()}
                  <div className="text-xs text-muted-foreground">
                    {(a?.creator?.nome_completo as string | undefined) || "—"} • {new Date(a.created_at).toLocaleString()}
                  </div>
                </DropdownMenuItem>
              );
            })}
          </div>
        )}
        <div className="p-2 border-t flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={handleMarkAllRead}>Marcar tudo como lida</Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
